import {
  Alert,
  Button,
  Card,
  Descriptions,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useRef, useState } from 'react';
import { Agent, ClientConfig } from '../../types';
import { getCrmLink } from '../agentSms/utils';
import { formatPhoneNumber } from '../../utils';
import { formatDateWithZone } from './format-date';

export interface OverviewEngagement {
  id: string;
  phone_number: string;
  is_active: boolean;
  lead_type?: string;
  action_plan_name?: string;
  campaign_key?: string | null;
  preferred_language?: string | null;
  engagement_zip_code?: string | null;
  lead?: {
    id: string;
    source_id: string;
    first_name: string;
    last_name: string;
    email: string;
    is_in_pond?: boolean | null;
    pond_id?: string | null;
    pond_name?: string | null;
    last_communication?: {
      created_at: string;
    };
  };
}

interface EngagementOverviewProps {
  agent?: Agent;
  clientConfig: ClientConfig | null;
  engagement: OverviewEngagement;
  onReengage: () => Promise<void>;
  onToggleActive: () => void;
  timezone: string;
  toggleLoading: boolean;
}

const Placeholder = () => (
  <Typography.Text type="secondary">N/A</Typography.Text>
);

export const EngagementOverview = ({
  agent,
  clientConfig,
  engagement,
  onReengage,
  onToggleActive,
  timezone,
  toggleLoading,
}: EngagementOverviewProps) => {
  const reengagePending = useRef(false);
  const [reengageStatus, setReengageStatus] = useState<
    'idle' | 'pending' | 'success' | 'error'
  >('idle');
  const crmLink = engagement.lead?.source_id
    ? getCrmLink(clientConfig, engagement.lead.source_id)
    : null;

  const handleReengage = async () => {
    if (reengagePending.current) {
      return;
    }

    reengagePending.current = true;
    setReengageStatus('pending');
    try {
      await onReengage();
      setReengageStatus('success');
    } catch {
      setReengageStatus('error');
    } finally {
      reengagePending.current = false;
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 16,
      }}
    >
      <Card
        extra={
          crmLink ? (
            <Button
              href={crmLink}
              icon={<ExportOutlined />}
              rel="noopener noreferrer"
              size="small"
              target="_blank"
              type="link"
            >
              CRM
            </Button>
          ) : undefined
        }
        size="small"
        title="Lead"
      >
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Name">
            {engagement.lead ? (
              `${engagement.lead.first_name} ${engagement.lead.last_name}`
            ) : (
              <Placeholder />
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {engagement.lead?.email || <Placeholder />}
          </Descriptions.Item>
          <Descriptions.Item label="Phone">
            <Typography.Text copyable={{ text: engagement.phone_number }}>
              {formatPhoneNumber(engagement.phone_number)}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Lead ID">
            <Typography.Text copyable style={{ fontSize: '12px' }}>
              {engagement.lead?.id}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Source ID">
            {engagement.lead?.source_id ? (
              <Typography.Text copyable style={{ fontSize: '12px' }}>
                {engagement.lead.source_id}
              </Typography.Text>
            ) : (
              <Placeholder />
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Type">
            {engagement.lead_type ? (
              <Tag color="blue">{engagement.lead_type}</Tag>
            ) : (
              <Placeholder />
            )}
          </Descriptions.Item>
          <Descriptions.Item label="In Pond">
            {engagement.lead?.is_in_pond == null ? (
              <Placeholder />
            ) : (
              <Tag color={engagement.lead.is_in_pond ? 'gold' : 'default'}>
                {engagement.lead.is_in_pond ? 'Yes' : 'No'}
              </Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Pond Name">
            {engagement.lead?.pond_name || <Placeholder />}
          </Descriptions.Item>
          <Descriptions.Item label="Pond ID">
            {engagement.lead?.pond_id ? (
              <Typography.Text copyable style={{ fontSize: '12px' }}>
                {engagement.lead.pond_id}
              </Typography.Text>
            ) : (
              <Placeholder />
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="Agent">
        {agent ? (
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Name">
              {agent.first_name} {agent.last_name}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {agent.phone ? (
                <Typography.Text copyable={{ text: agent.phone }}>
                  {formatPhoneNumber(agent.phone)}
                </Typography.Text>
              ) : (
                <Placeholder />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Timezone">
              {agent.timezone ? (
                <Tag color="blue">{agent.timezone}</Tag>
              ) : (
                <Placeholder />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Agent ID">
              <Typography.Text copyable style={{ fontSize: '12px' }}>
                {agent.id}
              </Typography.Text>
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Typography.Text type="secondary">No agent assigned</Typography.Text>
        )}
      </Card>

      <Card size="small" title="Engagement">
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Status">
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <Tag color={engagement.is_active ? 'success' : 'error'}>
                {engagement.is_active ? 'Active' : 'Inactive'}
              </Tag>
              <Button
                loading={toggleLoading}
                onClick={onToggleActive}
                size="small"
                type={engagement.is_active ? 'default' : 'primary'}
              >
                {engagement.is_active ? 'Turn Off' : 'Turn On'}
              </Button>
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Re-evaluate">
            <div
              style={{
                alignItems: 'flex-start',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <Button
                disabled={reengageStatus === 'pending'}
                loading={reengageStatus === 'pending'}
                onClick={() => void handleReengage()}
                size="small"
              >
                {reengageStatus === 'pending'
                  ? 'Re-evaluating...'
                  : 'Re-evaluate lead NOW'}
              </Button>
              {reengageStatus === 'success' && (
                <Alert
                  message="Lead queued for re-evaluation."
                  showIcon
                  type="success"
                />
              )}
              {reengageStatus === 'error' && (
                <Alert
                  message="Failed to queue lead. Try again."
                  showIcon
                  type="error"
                />
              )}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="Action Plan">
            {engagement.action_plan_name || <Placeholder />}
          </Descriptions.Item>
          <Descriptions.Item label="Campaign">
            {engagement.campaign_key ? (
              <Tag color="geekblue">
                {engagement.campaign_key.replace(/_/g, ' ')}
              </Tag>
            ) : (
              <Placeholder />
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Zip">
            {engagement.engagement_zip_code || <Placeholder />}
          </Descriptions.Item>
          <Descriptions.Item label="Language">
            {engagement.preferred_language || 'en'}
          </Descriptions.Item>
          <Descriptions.Item label="Last Contacted">
            {engagement.lead?.last_communication ? (
              <Tooltip title={timezone}>
                <span>
                  {formatDateWithZone(
                    engagement.lead.last_communication.created_at,
                    timezone,
                  )}
                </span>
              </Tooltip>
            ) : (
              <Placeholder />
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};
